// ============================================================
// DEBUTS CROP
// src/pages/profile/StoresManager.jsx
//
// ADMINISTRACIÓN CENTRAL DE TIENDAS
// PREMIUM NEON / RETRO TROPICAL
//
// FUNCIONES:
// - Listado, búsqueda y filtros
// - Creación y edición de tiendas
// - Logo y banner
// - Información comercial y ubicación
// - Métricas disponibles en la API
// - Detalle de tienda
// - Activar / desactivar
// - Destacar / quitar destacado
// - Verificar / quitar verificación
// - Selección individual y masiva
// - Operaciones masivas con resultados parciales
// - Exportación CSV compatible con Excel
// - Protección de peticiones y errores
//
// IMPORTANTE:
// Los permisos definitivos deben validarse en Django.
// La eliminación definitiva y el cambio de propietario
// requieren soporte específico en el backend.
// ============================================================

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Activity,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  Clock,
  Edit3,
  ExternalLink,
  Eye,
  EyeOff,
  Globe,
  ImagePlus,
  LoaderCircle,
  Mail,
  MapPin,
  MapPinned,
  Phone,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  Trash2,
  Upload,
  UserRound,
  X,
  XCircle,
  Download,
  CheckSquare,
  Square,
  Users,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import ProfilePageLayout from "../../components/profile/ProfilePageLayout";
import "../../styles/stores-manager.css";


// ==========================================================
// DEBUTS CROP — BIOLINK
// React + Django REST Framework
// Desarrollo y producción
// ==========================================================

// Desarrollo:
// VITE_API_URL=http://localhost:8000/api
// VITE_BACKEND_URL=http://localhost:8000
//
// Producción:
// VITE_API_URL=https://api.debutscrop.com/api
// VITE_BACKEND_URL=https://api.debutscrop.com

const API_BASE_URL = (
  import.meta.env.VITE_API_URL || "/api"
).replace(/\/+$/, "");

const STORES_ENDPOINT = "/stores/";
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;



const EMPTY_FORM = {
  name: "",
  description: "",
  logo: null,
  banner: null,
  email: "",
  phone: "",
  website: "",
  instagram: "",
  facebook: "",
  address: "",
  city: "",
  state: "",
  country: "Colombia",
  postal_code: "",
  latitude: "",
  longitude: "",
  currency: "COP",
  commission_percentage: "0",
  opening_time: "",
  closing_time: "",
  is_active: true,
  is_featured: false,
  is_verified: false,
};

const STORE_FLAGS = {
  is_active: {
    label: "estado",
    activate: "activar",
    deactivate: "desactivar",
  },
  is_featured: {
    label: "destacado",
    activate: "destacar",
    deactivate: "quitar el destacado de",
  },
  is_verified: {
    label: "verificación",
    activate: "verificar",
    deactivate: "quitar la verificación de",
  },
};

// ============================================================
// AUTENTICACIÓN
// ============================================================

function readAuth() {
  try {
    const raw = localStorage.getItem("auth");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readToken() {
  const auth = readAuth();

  return (
    auth?.access ||
    auth?.access_token ||
    auth?.token ||
    null
  );
}

// ============================================================
// NORMALIZACIÓN DE RESPUESTAS DJANGO REST FRAMEWORK
// ============================================================

function normalizeList(response) {
  let data = response;

  for (let depth = 0; depth < 5; depth += 1) {
    if (Array.isArray(data)) {
      return data;
    }

    if (!data || typeof data !== "object") {
      return [];
    }

    if (Array.isArray(data.results)) {
      return data.results;
    }

    if (Array.isArray(data.stores)) {
      return data.stores;
    }

    if (Array.isArray(data.data)) {
      return data.data;
    }

    if (data.results && typeof data.results === "object") {
      data = data.results;
      continue;
    }

    if (data.data && typeof data.data === "object") {
      data = data.data;
      continue;
    }

    break;
  }

  return [];
}

function normalizeObject(response) {
  let data = response;

  for (let depth = 0; depth < 5; depth += 1) {
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      return null;
    }

    if (data.id !== undefined && data.id !== null) {
      return data;
    }

    if (data.data && typeof data.data === "object") {
      data = data.data;
      continue;
    }

    if (data.store && typeof data.store === "object") {
      data = data.store;
      continue;
    }

    break;
  }

  return data && typeof data === "object" ? data : null;
}

// ============================================================
// ERRORES API
// ============================================================

function getErrorMessage(data, fallback) {
  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (typeof data?.detail === "string") {
    return data.detail;
  }

  if (typeof data?.message === "string") {
    return data.message;
  }

  if (typeof data?.error === "string") {
    return data.error;
  }

  if (data && typeof data === "object") {
    const ignored = ["success", "status", "code"];

    const key = Object.keys(data).find(
      (item) => !ignored.includes(item)
    );

    const value = key ? data[key] : null;

    if (Array.isArray(value) && value.length) {
      return `${key}: ${String(value[0])}`;
    }

    if (typeof value === "string") {
      return `${key}: ${value}`;
    }

    if (value && typeof value === "object") {
      const nestedKey = Object.keys(value)[0];
      const nestedValue = value[nestedKey];

      if (Array.isArray(nestedValue) && nestedValue.length) {
        return `${key}.${nestedKey}: ${nestedValue[0]}`;
      }

      if (typeof nestedValue === "string") {
        return `${key}.${nestedKey}: ${nestedValue}`;
      }
    }
  }

  return fallback;
}

// ============================================================
// FORMATO
// ============================================================

function money(value, currency = "COP") {
  const amount = Number(value || 0);

  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: currency || "COP",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return new Intl.NumberFormat("es-CO", {
      maximumFractionDigits: 2,
    }).format(amount);
  }
}

function number(value) {
  return new Intl.NumberFormat("es-CO").format(
    Number(value || 0)
  );
}

function formatDate(value) {
  if (!value) return "No disponible";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "No disponible";
  }

  return date.toLocaleString("es-CO");
}

function booleanLabel(value) {
  return value ? "Sí" : "No";
}

// ============================================================
// IMÁGENES Y UBICACIÓN
// ============================================================

function mediaUrl(value) {
  if (!value) return "";

  if (typeof value === "object") {
    value = value.url || value.image || value.src || "";
  }

  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  if (/^(https?:|data:|blob:)/i.test(value)) {
    return value;
  }

  if (value.startsWith("/")) {
    try {
      return `${new URL(API_BASE_URL).origin}${value}`;
    } catch {
      return value;
    }
  }

  return value;
}

function storeAddress(store) {
  return [
    store?.address,
    store?.city,
    store?.state,
    store?.postal_code,
    store?.country,
  ]
    .filter((value) => value !== null && value !== undefined)
    .map((value) => String(value).trim())
    .filter(Boolean)
    .join(", ");
}

function mapsUrl(store) {
  const latitude = store?.latitude;
  const longitude = store?.longitude;

  if (
    latitude !== null &&
    latitude !== undefined &&
    longitude !== null &&
    longitude !== undefined &&
    latitude !== "" &&
    longitude !== ""
  ) {
    return (
      "https://www.google.com/maps/search/?api=1&query=" +
      encodeURIComponent(`${latitude},${longitude}`)
    );
  }

  const address = storeAddress(store);

  if (!address) return "";

  return (
    "https://www.google.com/maps/search/?api=1&query=" +
    encodeURIComponent(address)
  );
}

function ownerName(store) {
  return (
    store?.owner_username ||
    store?.user_username ||
    store?.owner?.username ||
    store?.user?.username ||
    "Propietario no disponible"
  );
}

function ownerEmail(store) {
  return (
    store?.owner_email ||
    store?.owner?.email ||
    store?.user?.email ||
    "No disponible"
  );
}

function fileValidation(file) {
  if (!file) return "";

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
  ];

  if (!allowedTypes.includes(file.type)) {
    return "Usa una imagen JPG, PNG o WEBP.";
  }

  if (file.size > MAX_IMAGE_SIZE) {
    return "La imagen no puede superar los 10 MB.";
  }

  return "";
}

// ============================================================
// COMPONENTE
// ============================================================

export default function StoresManager() {
  const navigate = useNavigate();

  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const temporaryUrlsRef = useRef(new Set());

  // ----------------------------------------------------------
  // ESTADOS PRINCIPALES
  // ----------------------------------------------------------

  const [stores, setStores] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusLoading, setStatusLoading] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // ----------------------------------------------------------
  // SELECCIÓN Y OPERACIONES MASIVAS
  // ----------------------------------------------------------

  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // ----------------------------------------------------------
  // FILTROS
  // ----------------------------------------------------------

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");
  const [verifiedFilter, setVerifiedFilter] = useState("all");

  // ----------------------------------------------------------
  // NAVEGACIÓN Y FORMULARIO
  // ----------------------------------------------------------

  const [activeTab, setActiveTab] = useState("list");
  const [showForm, setShowForm] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);

  const [form, setForm] = useState({ ...EMPTY_FORM });

  const [previews, setPreviews] = useState({
    logo: "",
    banner: "",
  });

  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeBanner, setRemoveBanner] = useState(false);

  // ==========================================================
  // REQUEST CENTRAL
  // ==========================================================

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    const token = readToken();

    if (!token) {
      navigate("/login", { replace: true });

      throw new Error(
        "Debes iniciar sesión para administrar las tiendas."
      );
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };

    if (
      options.body &&
      !(options.body instanceof FormData) &&
      !headers["Content-Type"]
    ) {
      headers["Content-Type"] = "application/json";
    }

    let response;

    try {
      response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });
    } catch {
      throw new Error(
        "No fue posible conectar con Django. Verifica el backend y VITE_API_URL."
      );
    }

    if (response.status === 401) {
      localStorage.removeItem("auth");
      localStorage.removeItem("user");

      window.dispatchEvent(new Event("authUpdated"));

      navigate("/login", { replace: true });

      throw new Error(
        "Tu sesión expiró. Inicia sesión nuevamente."
      );
    }

    const contentType = response.headers.get("content-type") || "";
    let data = null;

    if (response.status !== 204) {
      if (contentType.includes("application/json")) {
        data = await response.json().catch(() => null);
      } else {
        data = await response.text().catch(() => "");
      }
    }

    if (!response.ok) {
      throw new Error(
        getErrorMessage(
          data,
          `Error ${response.status} al consultar ${endpoint}.`
        )
      );
    }

    return data;
  }, [navigate]);

  // ==========================================================
  // LIMPIEZA DE PREVISUALIZACIONES
  // ==========================================================

  const revokePreview = useCallback((url) => {
    if (url && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
      temporaryUrlsRef.current.delete(url);
    }
  }, []);

  const resetPreviews = useCallback(() => {
    setPreviews((previous) => {
      revokePreview(previous.logo);
      revokePreview(previous.banner);

      return {
        logo: "",
        banner: "",
      };
    });

    setRemoveLogo(false);
    setRemoveBanner(false);
  }, [revokePreview]);

  useEffect(() => {
    const temporaryUrls = temporaryUrlsRef.current;

    return () => {
      temporaryUrls.forEach((url) => {
        URL.revokeObjectURL(url);
      });

      temporaryUrls.clear();
    };
  }, []);

  // ==========================================================
  // CARGAR TIENDAS
  // ==========================================================

  const loadStores = useCallback(async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      const response = await apiRequest(STORES_ENDPOINT);
      const list = normalizeList(response);

      setStores(list);

      const validIds = new Set(
        list
          .map((store) => store.id)
          .filter((id) => id !== null && id !== undefined)
      );

      setSelectedStoreIds((previous) =>
        previous.filter((id) => validIds.has(id))
      );

      if (!Array.isArray(response) && list.length === 0) {
        const message = response?.detail || response?.message;

        if (message) {
          setError(String(message));
        }
      }
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No fue posible cargar las tiendas."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  // ==========================================================
  // RESUMEN GENERAL
  // ==========================================================

  const summary = useMemo(() => {
    const active = stores.filter(
      (store) => Boolean(store.is_active)
    ).length;

    const featured = stores.filter(
      (store) => Boolean(store.is_featured)
    ).length;

    const verified = stores.filter(
      (store) => Boolean(store.is_verified)
    ).length;

    return {
      total: stores.length,
      active,
      inactive: stores.length - active,
      featured,
      verified,
    };
  }, [stores]);

  // ==========================================================
  // BÚSQUEDA Y FILTROS
  // ==========================================================

  const filteredStores = useMemo(() => {
    const query = search.trim().toLowerCase();

    return stores.filter((store) => {
      const searchable = [
        store.id,
        store.name,
        store.slug,
        store.description,
        store.email,
        store.phone,
        store.city,
        store.state,
        store.country,
        store.address,
        store.owner_username,
        store.owner_email,
        store.user_username,
        store.owner?.username,
        store.user?.username,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLowerCase();

      if (query && !searchable.includes(query)) {
        return false;
      }

      if (
        statusFilter === "active" &&
        !store.is_active
      ) {
        return false;
      }

      if (
        statusFilter === "inactive" &&
        store.is_active
      ) {
        return false;
      }

      if (
        featuredFilter === "yes" &&
        !store.is_featured
      ) {
        return false;
      }

      if (
        featuredFilter === "no" &&
        store.is_featured
      ) {
        return false;
      }

      if (
        verifiedFilter === "yes" &&
        !store.is_verified
      ) {
        return false;
      }

      if (
        verifiedFilter === "no" &&
        store.is_verified
      ) {
        return false;
      }

      return true;
    });
  }, [
    stores,
    search,
    statusFilter,
    featuredFilter,
    verifiedFilter,
  ]);

  // ==========================================================
  // SELECCIÓN MASIVA
  // ==========================================================

  const visibleStoreIds = useMemo(
    () =>
      filteredStores
        .map((store) => store.id)
        .filter((id) => id !== null && id !== undefined),
    [filteredStores]
  );

  const allVisibleSelected =
    visibleStoreIds.length > 0 &&
    visibleStoreIds.every(
      (id) => selectedStoreIds.includes(id)
    );

  const selectedVisibleCount = visibleStoreIds.filter(
    (id) => selectedStoreIds.includes(id)
  ).length;

  const toggleStoreSelection = (storeId) => {
    setSelectedStoreIds((previous) =>
      previous.includes(storeId)
        ? previous.filter((id) => id !== storeId)
        : [...previous, storeId]
    );
  };

  const toggleVisibleStores = () => {
    setSelectedStoreIds((previous) => {
      if (allVisibleSelected) {
        return previous.filter(
          (id) => !visibleStoreIds.includes(id)
        );
      }

      return [
        ...new Set([...previous, ...visibleStoreIds]),
      ];
    });
  };

  const clearStoreSelection = () => {
    setSelectedStoreIds([]);
  };

  // ==========================================================
  // EXPORTAR TIENDAS A CSV
  // ==========================================================

  const exportStoresCSV = () => {
    const rows = filteredStores;

    if (!rows.length) {
      setError(
        "No hay tiendas para exportar con los filtros actuales."
      );
      return;
    }

    const columns = [
      ["id", "ID"],
      ["name", "Nombre"],
      ["slug", "Slug"],
      ["owner_username", "Propietario"],
      ["owner_email", "Correo del propietario"],
      ["email", "Correo comercial"],
      ["phone", "Teléfono"],
      ["website", "Sitio web"],
      ["instagram", "Instagram"],
      ["facebook", "Facebook"],
      ["address", "Dirección"],
      ["city", "Ciudad"],
      ["state", "Departamento"],
      ["country", "País"],
      ["postal_code", "Código postal"],
      ["currency", "Moneda"],
      ["commission_percentage", "Comisión"],
      ["is_active", "Activa"],
      ["is_featured", "Destacada"],
      ["is_verified", "Verificada"],
      ["views", "Vistas"],
      ["sales_count", "Ventas"],
      ["total_orders", "Pedidos"],
      ["total_products", "Productos"],
      ["rating", "Calificación"],
      ["opening_time", "Apertura"],
      ["closing_time", "Cierre"],
      ["created_at", "Creada"],
      ["updated_at", "Actualizada"],
    ];

    const escapeCSV = (value) => {
      if (value === null || value === undefined) {
        return '""';
      }

      if (typeof value === "boolean") {
        value = value ? "Sí" : "No";
      }

      const normalized = String(value).replace(/"/g, '""');

      return `"${normalized}"`;
    };

    const header = columns
      .map(([, label]) => escapeCSV(label))
      .join(";");

    const body = rows.map((store) =>
      columns
        .map(([key]) => {
          if (key === "owner_username") {
            return escapeCSV(ownerName(store));
          }

          if (key === "owner_email") {
            return escapeCSV(ownerEmail(store));
          }

          return escapeCSV(store[key]);
        })
        .join(";")
    );

    const csv = "\uFEFF" + [header, ...body].join("\r\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `debutscrop-tiendas-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);

    setError("");
    setNotice(
      `Exportación completada: ${rows.length} tienda(s) exportada(s).`
    );
  };

  // ==========================================================
  // OPERACIONES MASIVAS
  // ==========================================================

  const bulkUpdateStores = async (
    field,
    value,
    actionLabel
  ) => {
    if (bulkLoading || statusLoading !== null) {
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(STORE_FLAGS, field)) {
      setError("La operación solicitada no está permitida.");
      return;
    }

    if (selectedStoreIds.length === 0) {
      setError("Selecciona al menos una tienda.");
      return;
    }

    const selectedIds = [...selectedStoreIds];

    const confirmed = window.confirm(
      `¿Deseas ${actionLabel} ${selectedIds.length} tienda(s) seleccionada(s)?`
    );

    if (!confirmed) return;

    setBulkLoading(true);
    setError("");
    setNotice("");

    try {
      const results = await Promise.allSettled(
        selectedIds.map((id) =>
          apiRequest(`${STORES_ENDPOINT}${id}/`, {
            method: "PATCH",
            body: JSON.stringify({
              [field]: value,
            }),
          })
        )
      );

      const succeededIds = [];
      const failedIds = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          succeededIds.push(selectedIds[index]);
        } else {
          failedIds.push(selectedIds[index]);
        }
      });

      await loadStores(true);

      // Mantener seleccionadas solamente las operaciones fallidas.
      setSelectedStoreIds(failedIds);

      if (succeededIds.length > 0) {
        setStores((previous) =>
          previous.map((store) =>
            succeededIds.includes(store.id)
              ? { ...store, [field]: value }
              : store
          )
        );
      }

      if (failedIds.length === 0) {
        setNotice(
          `Operación completada: ${succeededIds.length} tienda(s) actualizada(s).`
        );
      } else {
        setError(
          `Se actualizaron ${succeededIds.length} tienda(s), pero ${failedIds.length} operación(es) fallaron. Las tiendas fallidas siguen seleccionadas para que puedas revisarlas.`
        );
      }
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No fue posible completar la operación masiva."
      );
    } finally {
      setBulkLoading(false);
    }
  };

  // ==========================================================
  // FORMULARIO: CREAR
  // ==========================================================

  const openCreateForm = () => {
    resetPreviews();

    setEditingStore(null);
    setForm({ ...EMPTY_FORM });

    setError("");
    setNotice("");

    setSelectedStore(null);
    setShowForm(true);
    setActiveTab("form");
  };

  // ==========================================================
  // FORMULARIO: EDITAR
  // ==========================================================

  const openEditForm = (store) => {
    resetPreviews();

    setEditingStore(store);

    setForm({
      ...EMPTY_FORM,

      name: store.name || "",
      description: store.description || "",

      email: store.email || "",
      phone: store.phone || "",
      website: store.website || "",
      instagram: store.instagram || "",
      facebook: store.facebook || "",

      address: store.address || "",
      city: store.city || "",
      state: store.state || "",
      country: store.country || "Colombia",
      postal_code: store.postal_code || "",

      latitude: store.latitude ?? "",
      longitude: store.longitude ?? "",

      currency: store.currency || "COP",
      commission_percentage: String(
        store.commission_percentage ?? "0"
      ),

      opening_time: store.opening_time
        ? String(store.opening_time).slice(0, 5)
        : "",

      closing_time: store.closing_time
        ? String(store.closing_time).slice(0, 5)
        : "",

      is_active: Boolean(store.is_active),
      is_featured: Boolean(store.is_featured),
      is_verified: Boolean(store.is_verified),

      logo: null,
      banner: null,
    });

    setPreviews({
      logo: mediaUrl(store.logo),
      banner: mediaUrl(store.banner),
    });

    setRemoveLogo(false);
    setRemoveBanner(false);

    setError("");
    setNotice("");

    setSelectedStore(null);
    setShowForm(true);
    setActiveTab("form");
  };

  // ==========================================================
  // CERRAR FORMULARIO
  // ==========================================================

  const closeForm = () => {
    if (saving) return;

    resetPreviews();

    setShowForm(false);
    setEditingStore(null);
    setForm({ ...EMPTY_FORM });
    setActiveTab("list");
  };

  const changeField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // ==========================================================
  // CARGAR LOGO Y BANNER
  // ==========================================================

  const handleImageChange = (event, field) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const validationError = fileValidation(file);

    if (validationError) {
      setError(validationError);
      event.target.value = "";
      return;
    }

    setError("");

    const previewUrl = URL.createObjectURL(file);
    temporaryUrlsRef.current.add(previewUrl);

    setForm((previous) => ({
      ...previous,
      [field]: file,
    }));

    if (field === "logo") {
      setRemoveLogo(false);
    }

    if (field === "banner") {
      setRemoveBanner(false);
    }

    setPreviews((previous) => {
      revokePreview(previous[field]);

      return {
        ...previous,
        [field]: previewUrl,
      };
    });

    event.target.value = "";
  };

  // ==========================================================
  // QUITAR IMAGEN
  // ==========================================================

  const clearImage = (field) => {
    setPreviews((previous) => {
      revokePreview(previous[field]);

      return {
        ...previous,
        [field]: "",
      };
    });

    setForm((previous) => ({
      ...previous,
      [field]: null,
    }));

    if (field === "logo") {
      setRemoveLogo(true);

      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }

    if (field === "banner") {
      setRemoveBanner(true);

      if (bannerInputRef.current) {
        bannerInputRef.current.value = "";
      }
    }
  };

  // ==========================================================
  // VALIDAR FORMULARIO
  // ==========================================================

  const validateForm = () => {
    if (!form.name.trim()) {
      return "El nombre de la tienda es obligatorio.";
    }

    if (form.name.trim().length > 120) {
      return "El nombre no puede superar los 120 caracteres.";
    }

    const commission = Number(form.commission_percentage);

    if (
      !Number.isFinite(commission) ||
      commission < 0 ||
      commission > 100
    ) {
      return "La comisión debe estar entre 0 y 100.";
    }

    const latitude =
      form.latitude === "" ? null : Number(form.latitude);

    const longitude =
      form.longitude === "" ? null : Number(form.longitude);

    if (
      latitude !== null &&
      (!Number.isFinite(latitude) ||
        latitude < -90 ||
        latitude > 90)
    ) {
      return "La latitud debe estar entre -90 y 90.";
    }

    if (
      longitude !== null &&
      (!Number.isFinite(longitude) ||
        longitude < -180 ||
        longitude > 180)
    ) {
      return "La longitud debe estar entre -180 y 180.";
    }

    if (
      (latitude === null) !==
      (longitude === null)
    ) {
      return "Debes introducir tanto la latitud como la longitud.";
    }

    if (
      form.opening_time &&
      form.closing_time &&
      form.opening_time === form.closing_time
    ) {
      return "La hora de apertura y la de cierre no pueden ser iguales.";
    }

    return "";
  };

  // ==========================================================
  // GUARDAR TIENDA
  // ==========================================================

  const saveStore = async (event) => {
    event.preventDefault();

    setError("");
    setNotice("");

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = new FormData();

    payload.append("name", form.name.trim());
    payload.append("description", form.description || "");

    payload.append("email", form.email || "");
    payload.append("phone", form.phone || "");
    payload.append("website", form.website || "");
    payload.append("instagram", form.instagram || "");
    payload.append("facebook", form.facebook || "");

    payload.append("address", form.address || "");
    payload.append("city", form.city || "");
    payload.append("state", form.state || "");
    payload.append("country", form.country || "Colombia");
    payload.append("postal_code", form.postal_code || "");

    payload.append(
      "latitude",
      form.latitude === "" ? "" : String(Number(form.latitude))
    );

    payload.append(
      "longitude",
      form.longitude === "" ? "" : String(Number(form.longitude))
    );

    payload.append("currency", form.currency || "COP");

    payload.append(
      "commission_percentage",
      String(Number(form.commission_percentage))
    );

    payload.append("opening_time", form.opening_time || "");
    payload.append("closing_time", form.closing_time || "");

    payload.append("is_active", String(Boolean(form.is_active)));
    payload.append("is_featured", String(Boolean(form.is_featured)));
    payload.append("is_verified", String(Boolean(form.is_verified)));

    if (form.logo instanceof File) {
      payload.append("logo", form.logo);
    } else if (removeLogo && editingStore?.logo) {
      payload.append("logo", "");
    }

    if (form.banner instanceof File) {
      payload.append("banner", form.banner);
    } else if (removeBanner && editingStore?.banner) {
      payload.append("banner", "");
    }

    const storeId = editingStore?.id;

    const endpoint = storeId
      ? `${STORES_ENDPOINT}${storeId}/`
      : STORES_ENDPOINT;

    setSaving(true);

    try {
      const response = await apiRequest(endpoint, {
        method: storeId ? "PATCH" : "POST",
        body: payload,
      });

      const savedStore = normalizeObject(response);

      setNotice(
        storeId
          ? "Tienda actualizada correctamente."
          : "Solicitud de creación enviada correctamente."
      );

      setShowForm(false);
      setEditingStore(null);
      setForm({ ...EMPTY_FORM });
      resetPreviews();
      setActiveTab("list");

      await loadStores(true);

      if (savedStore?.id) {
        setSelectedStore(savedStore);
      }
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No fue posible guardar la tienda."
      );
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================
  // CAMBIAR ESTADO INDIVIDUAL
  // ==========================================================

  const updateStoreFlag = async (store, field) => {
    if (!store?.id || statusLoading !== null || bulkLoading) {
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(STORE_FLAGS, field)) {
      setError("El estado solicitado no es válido.");
      return;
    }

    const nextValue = !store[field];
    const config = STORE_FLAGS[field];

    setStatusLoading(`${store.id}:${field}`);
    setError("");
    setNotice("");

    try {
      await apiRequest(`${STORES_ENDPOINT}${store.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          [field]: nextValue,
        }),
      });

      setStores((previous) =>
        previous.map((item) =>
          item.id === store.id
            ? { ...item, [field]: nextValue }
            : item
        )
      );

      setSelectedStore((previous) =>
        previous?.id === store.id
          ? { ...previous, [field]: nextValue }
          : previous
      );

      setNotice(
        `Se actualizó el ${config.label} de ${store.name}.`
      );
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No fue posible actualizar el estado de la tienda."
      );
    } finally {
      setStatusLoading(null);
    }
  };

  // ==========================================================
  // NAVEGACIÓN
  // ==========================================================

  const openList = () => {
    setShowForm(false);
    setSelectedStore(null);
    setActiveTab("list");
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <ProfilePageLayout
      title="Administración de tiendas"
      subtitle="Control central de tiendas, propietarios, ubicación e identidad comercial."
      icon={Store}
    >
      <main className="dc-stores-page">

        <button
          type="button"
          className="dc-stores-back"
          onClick={() => navigate("/profile")}
        >
          <ArrowLeft size={17} />
          VOLVER AL PERFIL
        </button>

        {/* HERO */}

        <section className="dc-stores-hero">
          <div className="dc-stores-hero-copy">
            <span className="dc-stores-eyebrow">
              DEBUTS CROP · STORE CONTROL CENTER
            </span>

            <h1>
              ADMINISTRACIÓN
              <span> DE TIENDAS</span>
            </h1>

            <p>
              Gestiona la identidad comercial, el contacto,
              la ubicación y el estado de cada tienda desde un solo lugar.
            </p>

            <div className="dc-stores-hero-actions">
              <button
                type="button"
                className="dc-stores-primary"
                onClick={openCreateForm}
              >
                <Plus size={17} />
                NUEVA TIENDA
              </button>

              <button
                type="button"
                className="dc-stores-secondary"
                onClick={() => loadStores(true)}
                disabled={refreshing || bulkLoading}
              >
                {refreshing
                  ? <LoaderCircle size={17} className="dc-stores-spin" />
                  : <RefreshCw size={17} />}
                ACTUALIZAR
              </button>
            </div>
          </div>

          <div className="dc-stores-hero-art">
            <div className="dc-stores-orbit one" />
            <div className="dc-stores-orbit two" />

            <div className="dc-stores-hero-icon">
              <Store size={52} strokeWidth={1.3} />
            </div>

            <span className="dc-stores-floating top">
              <BadgeCheck size={16} /> VERIFIED
            </span>

            <span className="dc-stores-floating bottom">
              <MapPinned size={16} /> LOCATION
            </span>
          </div>
        </section>

        {/* ALERTAS */}

        {error && (
          <div className="dc-stores-alert error" role="alert">
            <XCircle size={19} />
            <span>{error}</span>

            <button
              type="button"
              onClick={() => setError("")}
              aria-label="Cerrar error"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {notice && (
          <div className="dc-stores-alert success" role="status">
            <CheckCircle2 size={19} />
            <span>{notice}</span>

            <button
              type="button"
              onClick={() => setNotice("")}
              aria-label="Cerrar aviso"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {/* MÉTRICAS */}

        <section className="dc-stores-stats">
          <article className="dc-stores-stat">
            <div className="dc-stores-stat-icon purple">
              <Building2 size={21} />
            </div>
            <span>TIENDAS REGISTRADAS</span>
            <strong>{number(summary.total)}</strong>
            <small>Registros recibidos desde la API</small>
          </article>

          <article className="dc-stores-stat">
            <div className="dc-stores-stat-icon green">
              <CheckCircle2 size={21} />
            </div>
            <span>TIENDAS ACTIVAS</span>
            <strong>{number(summary.active)}</strong>
            <small>{number(summary.inactive)} inactivas</small>
          </article>

          <article className="dc-stores-stat">
            <div className="dc-stores-stat-icon orange">
              <Sparkles size={21} />
            </div>
            <span>TIENDAS DESTACADAS</span>
            <strong>{number(summary.featured)}</strong>
            <small>Marcadas para destacar</small>
          </article>

          <article className="dc-stores-stat">
            <div className="dc-stores-stat-icon blue">
              <BadgeCheck size={21} />
            </div>
            <span>TIENDAS VERIFICADAS</span>
            <strong>{number(summary.verified)}</strong>
            <small>Verificación registrada en Django</small>
          </article>
        </section>

        {/* PESTAÑAS */}

        <nav className="dc-stores-tabs">
          <button
            type="button"
            className={
              activeTab === "list" && !showForm ? "active" : ""
            }
            onClick={openList}
          >
            <Store size={17} /> TIENDAS
          </button>

          <button
            type="button"
            className={activeTab === "form" ? "active" : ""}
            onClick={() => {
              if (!showForm) openCreateForm();
            }}
          >
            <Plus size={17} /> NUEVA TIENDA
          </button>
        </nav>

        {/* ====================================================
            LISTADO DE TIENDAS
        ==================================================== */}

        {!loading && !showForm && activeTab !== "detail" && (
          <section className="dc-stores-panel">

            <div className="dc-stores-panel-heading">
              <div>
                <span className="dc-stores-eyebrow">
                  STORE DIRECTORY
                </span>
                <h2>Tiendas registradas</h2>
                <p>
                  Consulta propietarios, ubicación, estados y métricas disponibles.
                </p>
              </div>

              <span className="dc-stores-count">
                {filteredStores.length} TIENDAS
              </span>
            </div>

            {/* BÚSQUEDA Y FILTROS */}

            <div className="dc-stores-toolbar">
              <label className="dc-stores-search">
                <Search size={18} />

                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar tienda, propietario, correo o ciudad..."
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Limpiar búsqueda"
                  >
                    <X size={15} />
                  </button>
                )}
              </label>

              <label className="dc-stores-filter">
                <span>ESTADO</span>
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="all">Todos</option>
                  <option value="active">Activas</option>
                  <option value="inactive">Inactivas</option>
                </select>
              </label>

              <label className="dc-stores-filter">
                <span>DESTACADA</span>
                <select
                  value={featuredFilter}
                  onChange={(event) => setFeaturedFilter(event.target.value)}
                >
                  <option value="all">Todas</option>
                  <option value="yes">Sí</option>
                  <option value="no">No</option>
                </select>
              </label>

              <label className="dc-stores-filter">
                <span>VERIFICADA</span>
                <select
                  value={verifiedFilter}
                  onChange={(event) => setVerifiedFilter(event.target.value)}
                >
                  <option value="all">Todas</option>
                  <option value="yes">Sí</option>
                  <option value="no">No</option>
                </select>
              </label>
            </div>

            {/* ADMINISTRACIÓN MASIVA */}

            <section className="dc-stores-bulk-panel">
              <div className="dc-stores-bulk-heading">
                <div>
                  <span className="dc-stores-eyebrow">
                    BULK MANAGEMENT
                  </span>
                  <h3>
                    <Users size={18} />
                    Operaciones masivas
                  </h3>
                  <p>
                    Seleccionadas: <strong>{selectedStoreIds.length}</strong>
                    {" · "}
                    Visibles: <strong>{visibleStoreIds.length}</strong>
                    {" · "}
                    Seleccionadas visibles: <strong>{selectedVisibleCount}</strong>
                  </p>
                </div>

                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={toggleVisibleStores}
                  disabled={
                    bulkLoading ||
                    loading ||
                    visibleStoreIds.length === 0
                  }
                >
                  {allVisibleSelected
                    ? <CheckSquare size={16} />
                    : <Square size={16} />}
                  {allVisibleSelected
                    ? "DESELECCIONAR VISIBLES"
                    : "SELECCIONAR VISIBLES"}
                </button>
              </div>

              <div className="dc-stores-bulk-actions">
                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={exportStoresCSV}
                  disabled={loading || filteredStores.length === 0}
                >
                  <Download size={16} />
                  EXPORTAR CSV
                </button>

                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={() =>
                    bulkUpdateStores(
                      "is_active",
                      true,
                      "activar"
                    )
                  }
                  disabled={bulkLoading || selectedStoreIds.length === 0}
                >
                  <CheckCircle2 size={16} />
                  ACTIVAR
                </button>

                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={() =>
                    bulkUpdateStores(
                      "is_active",
                      false,
                      "desactivar"
                    )
                  }
                  disabled={bulkLoading || selectedStoreIds.length === 0}
                >
                  <EyeOff size={16} />
                  DESACTIVAR
                </button>

                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={() =>
                    bulkUpdateStores(
                      "is_featured",
                      true,
                      "destacar"
                    )
                  }
                  disabled={bulkLoading || selectedStoreIds.length === 0}
                >
                  <Sparkles size={16} />
                  DESTACAR
                </button>

                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={() =>
                    bulkUpdateStores(
                      "is_featured",
                      false,
                      "quitar el destacado de"
                    )
                  }
                  disabled={bulkLoading || selectedStoreIds.length === 0}
                >
                  <EyeOff size={16} />
                  QUITAR DESTACADO
                </button>

                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={() =>
                    bulkUpdateStores(
                      "is_verified",
                      true,
                      "verificar"
                    )
                  }
                  disabled={bulkLoading || selectedStoreIds.length === 0}
                >
                  <BadgeCheck size={16} />
                  VERIFICAR
                </button>

                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={() =>
                    bulkUpdateStores(
                      "is_verified",
                      false,
                      "quitar la verificación de"
                    )
                  }
                  disabled={bulkLoading || selectedStoreIds.length === 0}
                >
                  <ShieldCheck size={16} />
                  QUITAR VERIFICACIÓN
                </button>

                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={clearStoreSelection}
                  disabled={bulkLoading || selectedStoreIds.length === 0}
                >
                  <X size={16} />
                  LIMPIAR SELECCIÓN
                </button>
              </div>

              {bulkLoading && (
                <div className="dc-stores-bulk-progress" role="status">
                  <LoaderCircle
                    size={17}
                    className="dc-stores-spin"
                  />
                  Procesando operaciones. No cierres esta página.
                </div>
              )}
            </section>

            {/* RESULTADOS */}

            {filteredStores.length === 0 ? (
              <div className="dc-stores-empty">
                <Store size={43} strokeWidth={1.3} />

                <strong>
                  {stores.length === 0
                    ? "NO HAY TIENDAS RECIBIDAS"
                    : "NO HAY TIENDAS PARA MOSTRAR"}
                </strong>

                <span>
                  {stores.length === 0
                    ? "Comprueba que /api/stores/ devuelva una lista válida."
                    : "Ajusta los filtros o registra una nueva tienda."}
                </span>

                <button
                  type="button"
                  className="dc-stores-primary"
                  onClick={openCreateForm}
                >
                  <Plus size={17} /> CREAR TIENDA
                </button>
              </div>
            ) : (
              <div className="dc-stores-grid">
                {filteredStores.map((store) => {
                  const logo = mediaUrl(store.logo);
                  const banner = mediaUrl(store.banner);
                  const map = mapsUrl(store);
                  const isSelected = selectedStoreIds.includes(store.id);

                  return (
                    <article
                      className={`dc-stores-card ${
                        isSelected ? "dc-stores-card-selected" : ""
                      }`}
                      key={store.id}
                    >
                      {/* SELECTOR INDIVIDUAL */}

                      <label className="dc-stores-select-card">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleStoreSelection(store.id)}
                          disabled={bulkLoading || statusLoading !== null}
                          aria-label={`Seleccionar ${store.name}`}
                        />
                        <span>
                          {isSelected
                            ? <CheckSquare size={17} />
                            : <Square size={17} />}
                          SELECCIONAR
                        </span>
                      </label>

                      <div className="dc-stores-card-banner">
                        {banner ? (
                          <img
                            src={banner}
                            alt={`Banner de ${store.name}`}
                            loading="lazy"
                          />
                        ) : (
                          <div className="dc-stores-banner-placeholder">
                            <Store size={35} />
                          </div>
                        )}

                        <div className="dc-stores-card-flags">
                          <span
                            className={`dc-stores-status ${
                              store.is_active ? "active" : "inactive"
                            }`}
                          >
                            {store.is_active ? "ACTIVA" : "INACTIVA"}
                          </span>

                          {store.is_featured && (
                            <span className="dc-stores-flag featured">
                              <Sparkles size={12} /> DESTACADA
                            </span>
                          )}

                          {store.is_verified && (
                            <span className="dc-stores-flag verified">
                              <BadgeCheck size={12} /> VERIFICADA
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="dc-stores-card-content">
                        <div className="dc-stores-card-brand">
                          <div className="dc-stores-logo">
                            {logo ? (
                              <img
                                src={logo}
                                alt={`Logo de ${store.name}`}
                                loading="lazy"
                              />
                            ) : (
                              <Store size={26} />
                            )}
                          </div>

                          <div className="dc-stores-card-title">
                            <h3>{store.name}</h3>
                            <span>ID #{store.id}</span>
                          </div>
                        </div>

                        <p className="dc-stores-card-description">
                          {store.description ||
                            "Esta tienda aún no tiene descripción."}
                        </p>

                        <div className="dc-stores-card-meta">
                          <span>
                            <UserRound size={14} />
                            {ownerName(store)}
                          </span>

                          <span>
                            <MapPin size={14} />
                            {[
                              store.city,
                              store.state,
                              store.country,
                            ].filter(Boolean).join(", ") ||
                              "Ubicación pendiente"}
                          </span>

                          {store.email && (
                            <span>
                              <Mail size={14} />
                              {store.email}
                            </span>
                          )}

                          {store.phone && (
                            <span>
                              <Phone size={14} />
                              {store.phone}
                            </span>
                          )}
                        </div>

                        <div className="dc-stores-card-metrics">
                          <div>
                            <span>PRODUCTOS</span>
                            <strong>
                              {number(
                                store.products_count ??
                                store.total_products
                              )}
                            </strong>
                          </div>

                          <div>
                            <span>VENTAS</span>
                            <strong>
                              {number(
                                store.sales_count ??
                                store.paid_sales_count
                              )}
                            </strong>
                          </div>

                          <div>
                            <span>PEDIDOS</span>
                            <strong>
                              {number(
                                store.orders_count ??
                                store.total_orders
                              )}
                            </strong>
                          </div>

                          <div>
                            <span>CALIFICACIÓN</span>
                            <strong>
                              {Number(store.rating || 0).toFixed(1)} ★
                            </strong>
                          </div>
                        </div>

                        <div className="dc-stores-card-footer">
                          <button
                            type="button"
                            className="dc-stores-secondary"
                            onClick={() => {
                              setSelectedStore(store);
                              setShowForm(false);
                              setActiveTab("detail");
                            }}
                          >
                            <Eye size={16} /> DETALLE
                          </button>

                          <button
                            type="button"
                            className="dc-stores-primary"
                            onClick={() => openEditForm(store)}
                          >
                            <Edit3 size={16} /> EDITAR
                          </button>
                        </div>

                        <div className="dc-stores-quick-actions">
                          <button
                            type="button"
                            onClick={() =>
                              updateStoreFlag(store, "is_active")
                            }
                            disabled={
                              statusLoading !== null || bulkLoading
                            }
                          >
                            {store.is_active
                              ? <EyeOff size={14} />
                              : <CheckCircle2 size={14} />}
                            {store.is_active ? "Desactivar" : "Activar"}
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              updateStoreFlag(store, "is_featured")
                            }
                            disabled={
                              statusLoading !== null || bulkLoading
                            }
                          >
                            <Sparkles size={14} />
                            {store.is_featured
                              ? "Quitar destacado"
                              : "Destacar"}
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              updateStoreFlag(store, "is_verified")
                            }
                            disabled={
                              statusLoading !== null || bulkLoading
                            }
                          >
                            <BadgeCheck size={14} />
                            {store.is_verified
                              ? "Quitar verificación"
                              : "Verificar"}
                          </button>
                        </div>

                        {statusLoading === `${store.id}:is_active` && (
                          <small className="dc-stores-operation-status">
                            Actualizando estado...
                          </small>
                        )}

                        {statusLoading === `${store.id}:is_featured` && (
                          <small className="dc-stores-operation-status">
                            Actualizando destacado...
                          </small>
                        )}

                        {statusLoading === `${store.id}:is_verified` && (
                          <small className="dc-stores-operation-status">
                            Actualizando verificación...
                          </small>
                        )}

                        {map && (
                          <a
                            className="dc-stores-map-link"
                            href={map}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <MapPinned size={15} />
                            ABRIR EN GOOGLE MAPS
                            <ExternalLink size={13} />
                          </a>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* CARGANDO */}

        {loading && (
          <section className="dc-stores-panel dc-stores-loading">
            <LoaderCircle
              size={31}
              className="dc-stores-spin"
            />
            <strong>CARGANDO TIENDAS...</strong>
            <span>Consultando el backend de Debuts Crop</span>
          </section>
        )}

        {/* ====================================================
            FORMULARIO
        ==================================================== */}

        {showForm && (
          <section className="dc-stores-panel dc-stores-form-panel">
            <div className="dc-stores-panel-heading">
              <div>
                <span className="dc-stores-eyebrow">
                  {editingStore ? "STORE EDITOR" : "STORE CREATOR"}
                </span>

                <h2>
                  {editingStore
                    ? "Editar tienda"
                    : "Registrar nueva tienda"}
                </h2>

                <p>
                  El propietario y el slug se administran en Django.
                </p>
              </div>

              <button
                type="button"
                className="dc-stores-secondary"
                onClick={closeForm}
                disabled={saving}
              >
                <X size={16} /> CERRAR
              </button>
            </div>

            <form
              className="dc-stores-form"
              onSubmit={saveStore}
            >
              {/* INFORMACIÓN BÁSICA */}

              <section className="dc-stores-form-section">
                <div className="dc-stores-section-title">
                  <Building2 size={19} />
                  <div>
                    <h3>Información básica</h3>
                    <p>Nombre comercial y descripción pública.</p>
                  </div>
                </div>

                <div className="dc-stores-form-grid">
                  <label className="dc-stores-field full">
                    <span>NOMBRE DE LA TIENDA *</span>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(event) =>
                        changeField("name", event.target.value)
                      }
                      placeholder="Ej. Debuts Crop Premium Grow Shop"
                      maxLength={120}
                      required
                    />
                  </label>

                  <label className="dc-stores-field full">
                    <span>DESCRIPCIÓN</span>
                    <textarea
                      value={form.description}
                      onChange={(event) =>
                        changeField("description", event.target.value)
                      }
                      rows={5}
                      placeholder="Describe la tienda y su propuesta comercial..."
                    />
                  </label>
                </div>
              </section>

              {/* IDENTIDAD VISUAL */}

              <section className="dc-stores-form-section">
                <div className="dc-stores-section-title">
                  <ImagePlus size={19} />
                  <div>
                    <h3>Identidad visual</h3>
                    <p>Logo y banner de la tienda.</p>
                  </div>
                </div>

                <div className="dc-stores-image-grid">
                  <div className="dc-stores-image-editor">
                    <div className="dc-stores-image-heading">
                      <strong>LOGO</strong>
                      <span>Identidad principal</span>
                    </div>

                    <button
                      type="button"
                      className="dc-stores-image-upload logo"
                      onClick={() => logoInputRef.current?.click()}
                    >
                      {previews.logo ? (
                        <img
                          src={previews.logo}
                          alt="Vista previa del logo"
                        />
                      ) : (
                        <>
                          <Upload size={27} />
                          <strong>SUBIR LOGO</strong>
                          <span>JPG, PNG o WEBP · Máximo 10 MB</span>
                        </>
                      )}
                    </button>

                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="dc-stores-hidden-input"
                      onChange={(event) =>
                        handleImageChange(event, "logo")
                      }
                    />

                    {previews.logo && (
                      <button
                        type="button"
                        className="dc-stores-remove-image"
                        onClick={() => clearImage("logo")}
                      >
                        <Trash2 size={14} />
                        QUITAR IMAGEN
                      </button>
                    )}
                  </div>

                  <div className="dc-stores-image-editor">
                    <div className="dc-stores-image-heading">
                      <strong>BANNER</strong>
                      <span>Imagen de portada</span>
                    </div>

                    <button
                      type="button"
                      className="dc-stores-image-upload banner"
                      onClick={() => bannerInputRef.current?.click()}
                    >
                      {previews.banner ? (
                        <img
                          src={previews.banner}
                          alt="Vista previa del banner"
                        />
                      ) : (
                        <>
                          <ImagePlus size={27} />
                          <strong>SUBIR BANNER</strong>
                          <span>JPG, PNG o WEBP · Máximo 10 MB</span>
                        </>
                      )}
                    </button>

                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="dc-stores-hidden-input"
                      onChange={(event) =>
                        handleImageChange(event, "banner")
                      }
                    />

                    {previews.banner && (
                      <button
                        type="button"
                        className="dc-stores-remove-image"
                        onClick={() => clearImage("banner")}
                      >
                        <Trash2 size={14} />
                        QUITAR IMAGEN
                      </button>
                    )}
                  </div>
                </div>

                <div className="dc-stores-inline-note">
                  <ShieldCheck size={17} />
                  <span>
                    La eliminación de imágenes existentes depende de que el
                    serializador de Django permita limpiar esos campos.
                  </span>
                </div>
              </section>

              {/* CONTACTO */}

              <section className="dc-stores-form-section">
                <div className="dc-stores-section-title">
                  <Phone size={19} />
                  <div>
                    <h3>Contacto y redes</h3>
                    <p>Información comercial para los clientes.</p>
                  </div>
                </div>

                <div className="dc-stores-form-grid">
                  <label className="dc-stores-field">
                    <span>CORREO ELECTRÓNICO</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) =>
                        changeField("email", event.target.value)
                      }
                      placeholder="contacto@tienda.com"
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>TELÉFONO</span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(event) =>
                        changeField("phone", event.target.value)
                      }
                      placeholder="+57 300 000 0000"
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>SITIO WEB</span>
                    <input
                      type="url"
                      value={form.website}
                      onChange={(event) =>
                        changeField("website", event.target.value)
                      }
                      placeholder="https://mitienda.com"
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>INSTAGRAM · URL</span>
                    <input
                      type="url"
                      value={form.instagram}
                      onChange={(event) =>
                        changeField("instagram", event.target.value)
                      }
                      placeholder="https://instagram.com/mitienda"
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>FACEBOOK · URL</span>
                    <input
                      type="url"
                      value={form.facebook}
                      onChange={(event) =>
                        changeField("facebook", event.target.value)
                      }
                      placeholder="https://facebook.com/mitienda"
                    />
                  </label>
                </div>
              </section>

              {/* UBICACIÓN */}

              <section className="dc-stores-form-section">
                <div className="dc-stores-section-title">
                  <MapPinned size={19} />
                  <div>
                    <h3>Ubicación</h3>
                    <p>Dirección física y ubicación de Google Maps.</p>
                  </div>
                </div>

                <div className="dc-stores-form-grid">
                  <label className="dc-stores-field full">
                    <span>DIRECCIÓN</span>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(event) =>
                        changeField("address", event.target.value)
                      }
                      placeholder="Calle, carrera, número y local"
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>CIUDAD</span>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(event) =>
                        changeField("city", event.target.value)
                      }
                      placeholder="Medellín"
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>DEPARTAMENTO / ESTADO</span>
                    <input
                      type="text"
                      value={form.state}
                      onChange={(event) =>
                        changeField("state", event.target.value)
                      }
                      placeholder="Antioquia"
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>PAÍS</span>
                    <input
                      type="text"
                      value={form.country}
                      onChange={(event) =>
                        changeField("country", event.target.value)
                      }
                      placeholder="Colombia"
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>CÓDIGO POSTAL</span>
                    <input
                      type="text"
                      value={form.postal_code}
                      onChange={(event) =>
                        changeField("postal_code", event.target.value)
                      }
                      placeholder="Opcional"
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>LATITUD</span>
                    <input
                      type="number"
                      min="-90"
                      max="90"
                      step="any"
                      value={form.latitude}
                      onChange={(event) =>
                        changeField("latitude", event.target.value)
                      }
                      placeholder="Ej. 6.2088"
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>LONGITUD</span>
                    <input
                      type="number"
                      min="-180"
                      max="180"
                      step="any"
                      value={form.longitude}
                      onChange={(event) =>
                        changeField("longitude", event.target.value)
                      }
                      placeholder="Ej. -75.5674"
                    />
                  </label>
                </div>

                {(form.address ||
                  form.city ||
                  form.state ||
                  form.latitude ||
                  form.longitude) && (
                  <a
                    className="dc-stores-map-preview-link"
                    href={mapsUrl(form)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MapPin size={17} />
                    PREVISUALIZAR EN GOOGLE MAPS
                    <ExternalLink size={14} />
                  </a>
                )}
              </section>

              {/* CONFIGURACIÓN COMERCIAL */}

              <section className="dc-stores-form-section">
                <div className="dc-stores-section-title">
                  <Activity size={19} />
                  <div>
                    <h3>Configuración comercial</h3>
                    <p>Moneda y porcentaje de comisión de la tienda.</p>
                  </div>
                </div>

                <div className="dc-stores-form-grid">
                  <label className="dc-stores-field">
                    <span>MONEDA</span>
                    <select
                      value={form.currency}
                      onChange={(event) =>
                        changeField("currency", event.target.value)
                      }
                    >
                      <option value="COP">COP · Peso colombiano</option>
                      <option value="USD">USD · Dólar estadounidense</option>
                      <option value="EUR">EUR · Euro</option>
                    </select>
                  </label>

                  <label className="dc-stores-field">
                    <span>COMISIÓN (%)</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={form.commission_percentage}
                      onChange={(event) =>
                        changeField(
                          "commission_percentage",
                          event.target.value
                        )
                      }
                    />
                    <small>Valor permitido: de 0 a 100.</small>
                  </label>
                </div>
              </section>

              {/* HORARIOS */}

              <section className="dc-stores-form-section">
                <div className="dc-stores-section-title">
                  <Clock size={19} />
                  <div>
                    <h3>Horario de atención</h3>
                    <p>Hora de apertura y cierre.</p>
                  </div>
                </div>

                <div className="dc-stores-form-grid">
                  <label className="dc-stores-field">
                    <span>HORA DE APERTURA</span>
                    <input
                      type="time"
                      value={form.opening_time}
                      onChange={(event) =>
                        changeField("opening_time", event.target.value)
                      }
                    />
                  </label>

                  <label className="dc-stores-field">
                    <span>HORA DE CIERRE</span>
                    <input
                      type="time"
                      value={form.closing_time}
                      onChange={(event) =>
                        changeField("closing_time", event.target.value)
                      }
                    />
                  </label>
                </div>
              </section>

              {/* ESTADOS */}

              <section className="dc-stores-form-section">
                <div className="dc-stores-section-title">
                  <ShieldCheck size={19} />
                  <div>
                    <h3>Estado y visibilidad</h3>
                    <p>Los permisos definitivos corresponden al backend.</p>
                  </div>
                </div>

                <div className="dc-stores-toggles">
                  <label className="dc-stores-toggle">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(event) =>
                        changeField("is_active", event.target.checked)
                      }
                    />
                    <span className="dc-stores-toggle-ui" />
                    <span>
                      <strong>TIENDA ACTIVA</strong>
                      <small>Estado operativo de la tienda.</small>
                    </span>
                  </label>

                  <label className="dc-stores-toggle">
                    <input
                      type="checkbox"
                      checked={form.is_featured}
                      onChange={(event) =>
                        changeField("is_featured", event.target.checked)
                      }
                    />
                    <span className="dc-stores-toggle-ui" />
                    <span>
                      <strong>TIENDA DESTACADA</strong>
                      <small>Puede aparecer en espacios destacados.</small>
                    </span>
                  </label>

                  <label className="dc-stores-toggle">
                    <input
                      type="checkbox"
                      checked={form.is_verified}
                      onChange={(event) =>
                        changeField("is_verified", event.target.checked)
                      }
                    />
                    <span className="dc-stores-toggle-ui" />
                    <span>
                      <strong>TIENDA VERIFICADA</strong>
                      <small>Marca de verificación comercial.</small>
                    </span>
                  </label>
                </div>
              </section>

              {/* BOTONES */}

              <div className="dc-stores-form-actions">
                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={closeForm}
                  disabled={saving}
                >
                  CANCELAR
                </button>

                <button
                  type="submit"
                  className="dc-stores-primary"
                  disabled={saving}
                >
                  {saving
                    ? <LoaderCircle size={18} className="dc-stores-spin" />
                    : <CheckCircle2 size={18} />}

                  {saving
                    ? "GUARDANDO..."
                    : editingStore
                      ? "GUARDAR CAMBIOS"
                      : "CREAR TIENDA"}

                  {!saving && <ArrowRight size={17} />}
                </button>
              </div>
            </form>
          </section>
        )}

        {/* ====================================================
            DETALLE DE TIENDA
        ==================================================== */}

        {!showForm &&
          activeTab === "detail" &&
          selectedStore && (
            <section className="dc-stores-panel dc-stores-detail-panel">
              <div className="dc-stores-panel-heading">
                <div>
                  <span className="dc-stores-eyebrow">
                    STORE PROFILE · #{selectedStore.id}
                  </span>
                  <h2>{selectedStore.name}</h2>
                  <p>
                    Información disponible en el modelo y el serializador.
                  </p>
                </div>

                <button
                  type="button"
                  className="dc-stores-secondary"
                  onClick={openList}
                >
                  <ArrowLeft size={16} /> VOLVER
                </button>
              </div>

              <div className="dc-stores-detail-cover">
                {mediaUrl(selectedStore.banner) ? (
                  <img
                    src={mediaUrl(selectedStore.banner)}
                    alt={`Banner de ${selectedStore.name}`}
                  />
                ) : (
                  <div className="dc-stores-banner-placeholder">
                    <Store size={35} />
                  </div>
                )}
              </div>

              <div className="dc-stores-detail-brand">
                <div className="dc-stores-detail-logo">
                  {mediaUrl(selectedStore.logo) ? (
                    <img
                      src={mediaUrl(selectedStore.logo)}
                      alt={`Logo de ${selectedStore.name}`}
                    />
                  ) : (
                    <Store size={35} />
                  )}
                </div>

                <div>
                  <h3>{selectedStore.name}</h3>
                  <span>
                    {selectedStore.slug || `Tienda #${selectedStore.id}`}
                  </span>
                </div>

                <button
                  type="button"
                  className="dc-stores-primary"
                  onClick={() => openEditForm(selectedStore)}
                >
                  <Edit3 size={16} /> EDITAR TIENDA
                </button>
              </div>

              <div className="dc-stores-detail-badges">
                <span
                  className={`dc-stores-status ${
                    selectedStore.is_active ? "active" : "inactive"
                  }`}
                >
                  {selectedStore.is_active ? "ACTIVA" : "INACTIVA"}
                </span>

                {selectedStore.is_featured && (
                  <span className="dc-stores-flag featured">
                    <Sparkles size={13} /> DESTACADA
                  </span>
                )}

                {selectedStore.is_verified && (
                  <span className="dc-stores-flag verified">
                    <BadgeCheck size={13} /> VERIFICADA
                  </span>
                )}
              </div>

              {/* PROPIETARIO */}

              <div className="dc-stores-detail-section">
                <h3>
                  <UserRound size={18} /> Propietario
                </h3>

                <div className="dc-stores-detail-grid">
                  <div>
                    <span>USUARIO</span>
                    <strong>{ownerName(selectedStore)}</strong>
                  </div>

                  <div>
                    <span>CORREO DEL PROPIETARIO</span>
                    <strong>{ownerEmail(selectedStore)}</strong>
                  </div>

                  <div>
                    <span>ID DE TIENDA</span>
                    <strong>{selectedStore.id}</strong>
                  </div>

                  <div>
                    <span>SLUG</span>
                    <strong>{selectedStore.slug || "No disponible"}</strong>
                  </div>
                </div>
              </div>

              {/* COMERCIAL */}

              <div className="dc-stores-detail-section">
                <h3>
                  <Building2 size={18} /> Información comercial
                </h3>

                <p>
                  {selectedStore.description ||
                    "Sin descripción registrada."}
                </p>

                <div className="dc-stores-detail-grid">
                  <div>
                    <span>MONEDA</span>
                    <strong>{selectedStore.currency || "COP"}</strong>
                  </div>

                  <div>
                    <span>COMISIÓN</span>
                    <strong>
                      {number(selectedStore.commission_percentage)}%
                    </strong>
                  </div>

                  <div>
                    <span>CALIFICACIÓN</span>
                    <strong>
                      ★ {Number(selectedStore.rating || 0).toFixed(2)}
                    </strong>
                  </div>

                  <div>
                    <span>VISTAS</span>
                    <strong>{number(selectedStore.views)}</strong>
                  </div>

                  <div>
                    <span>PRODUCTOS REGISTRADOS</span>
                    <strong>
                      {number(
                        selectedStore.products_count ??
                        selectedStore.total_products
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>VENTAS REGISTRADAS</span>
                    <strong>{number(selectedStore.sales_count)}</strong>
                  </div>

                  <div>
                    <span>TOTAL DE PEDIDOS</span>
                    <strong>
                      {number(
                        selectedStore.orders_count ??
                        selectedStore.total_orders
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>VENTAS PAGADAS</span>
                    <strong>{number(selectedStore.paid_sales_count)}</strong>
                  </div>

                  <div>
                    <span>TOTAL VENDIDO</span>
                    <strong>
                      {money(
                        selectedStore.sales_total,
                        selectedStore.currency
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>PRODUCTOS ACTIVOS</span>
                    <strong>{number(selectedStore.active_products)}</strong>
                  </div>

                  <div>
                    <span>TRABAJADORES</span>
                    <strong>{number(selectedStore.workers_count)}</strong>
                  </div>

                  <div>
                    <span>ENTREGAS</span>
                    <strong>{number(selectedStore.deliveries_count)}</strong>
                  </div>
                </div>
              </div>

              {/* ESTADOS */}

              <div className="dc-stores-detail-section">
                <h3>
                  <ShieldCheck size={18} /> Estado y visibilidad
                </h3>

                <div className="dc-stores-detail-grid">
                  <div>
                    <span>ACTIVA</span>
                    <strong>{booleanLabel(selectedStore.is_active)}</strong>
                  </div>

                  <div>
                    <span>DESTACADA</span>
                    <strong>{booleanLabel(selectedStore.is_featured)}</strong>
                  </div>

                  <div>
                    <span>VERIFICADA</span>
                    <strong>{booleanLabel(selectedStore.is_verified)}</strong>
                  </div>
                </div>

                <div className="dc-stores-quick-actions">
                  <button
                    type="button"
                    onClick={() =>
                      updateStoreFlag(selectedStore, "is_active")
                    }
                    disabled={statusLoading !== null || bulkLoading}
                  >
                    {selectedStore.is_active
                      ? <EyeOff size={14} />
                      : <CheckCircle2 size={14} />}
                    {selectedStore.is_active ? "Desactivar" : "Activar"}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      updateStoreFlag(selectedStore, "is_featured")
                    }
                    disabled={statusLoading !== null || bulkLoading}
                  >
                    <Sparkles size={14} />
                    {selectedStore.is_featured
                      ? "Quitar destacado"
                      : "Destacar"}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      updateStoreFlag(selectedStore, "is_verified")
                    }
                    disabled={statusLoading !== null || bulkLoading}
                  >
                    <BadgeCheck size={14} />
                    {selectedStore.is_verified
                      ? "Quitar verificación"
                      : "Verificar"}
                  </button>
                </div>
              </div>

              {/* CONTACTO */}

              <div className="dc-stores-detail-section">
                <h3>
                  <Phone size={18} /> Contacto
                </h3>

                <div className="dc-stores-detail-grid">
                  <div>
                    <span>CORREO</span>
                    <strong>{selectedStore.email || "No registrado"}</strong>
                  </div>

                  <div>
                    <span>TELÉFONO</span>
                    <strong>{selectedStore.phone || "No registrado"}</strong>
                  </div>

                  <div>
                    <span>SITIO WEB</span>
                    {selectedStore.website ? (
                      <a
                        href={selectedStore.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Abrir sitio <ExternalLink size={13} />
                      </a>
                    ) : (
                      <strong>No registrado</strong>
                    )}
                  </div>

                  <div>
                    <span>INSTAGRAM</span>
                    {selectedStore.instagram ? (
                      <a
                        href={selectedStore.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Abrir Instagram <ExternalLink size={13} />
                      </a>
                    ) : (
                      <strong>No registrado</strong>
                    )}
                  </div>

                  <div>
                    <span>FACEBOOK</span>
                    {selectedStore.facebook ? (
                      <a
                        href={selectedStore.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Abrir Facebook <ExternalLink size={13} />
                      </a>
                    ) : (
                      <strong>No registrado</strong>
                    )}
                  </div>
                </div>
              </div>

              {/* UBICACIÓN */}

              <div className="dc-stores-detail-section">
                <h3>
                  <MapPin size={18} /> Ubicación
                </h3>

                <p>
                  {storeAddress(selectedStore) || "Sin dirección registrada"}
                </p>

                <div className="dc-stores-detail-grid">
                  <div>
                    <span>LATITUD</span>
                    <strong>
                      {selectedStore.latitude ?? "No registrada"}
                    </strong>
                  </div>

                  <div>
                    <span>LONGITUD</span>
                    <strong>
                      {selectedStore.longitude ?? "No registrada"}
                    </strong>
                  </div>

                  <div>
                    <span>APERTURA</span>
                    <strong>
                      {selectedStore.opening_time || "No registrada"}
                    </strong>
                  </div>

                  <div>
                    <span>CIERRE</span>
                    <strong>
                      {selectedStore.closing_time || "No registrada"}
                    </strong>
                  </div>
                </div>

                {mapsUrl(selectedStore) && (
                  <a
                    className="dc-stores-map-preview-link"
                    href={mapsUrl(selectedStore)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MapPinned size={17} />
                    ABRIR UBICACIÓN EN GOOGLE MAPS
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>

              {/* SISTEMA */}

              <div className="dc-stores-detail-section">
                <h3>
                  <Globe size={18} /> Sistema
                </h3>

                <div className="dc-stores-detail-grid">
                  <div>
                    <span>CREADA</span>
                    <strong>{formatDate(selectedStore.created_at)}</strong>
                  </div>

                  <div>
                    <span>ACTUALIZADA</span>
                    <strong>{formatDate(selectedStore.updated_at)}</strong>
                  </div>
                </div>
              </div>
            </section>
          )}

        {/* FOOTER */}

        <footer className="dc-stores-footer">
          <span>
            <ShieldCheck size={16} />
            DEBUTS CROP · STORE MANAGEMENT
          </span>

          <span>
            LOS PERMISOS Y LAS MÉTRICAS SE VALIDAN EN EL BACKEND
          </span>
        </footer>
      </main>
    </ProfilePageLayout>
  );
}